/** Field type catalogue + SchemaJson helpers for eSource Form Builder. */

export const FORM_DESCRIPTION_MAX_CHARS = 500;
export const FORM_HEADER_MAX_CHARS = 200;

export const WIDTH_OPTIONS = [
  { value: "full", label: "Full width" },
  { value: "half", label: "Half width" },
];

export const FORM_LAYOUTS = [
  { value: "two-column", label: "Two column" },
  { value: "one-column", label: "One column" },
];

export const FILL_ACCESS_INTERNAL = "internal";
export const FILL_ACCESS_ANYWHERE = "anywhere";

export const FILL_ACCESS_OPTIONS = [
  { value: FILL_ACCESS_INTERNAL, label: "Internal" },
  { value: FILL_ACCESS_ANYWHERE, label: "Anywhere" },
];

export function normalizeFillAccess(value) {
  return String(value || "").toLowerCase() === FILL_ACCESS_ANYWHERE
    ? FILL_ACCESS_ANYWHERE
    : FILL_ACCESS_INTERNAL;
}

export function normalizeFormHeader(value) {
  return String(value || "").slice(0, FORM_HEADER_MAX_CHARS);
}

export function normalizeFormDescription(value) {
  return String(value || "").slice(0, FORM_DESCRIPTION_MAX_CHARS);
}

export const FIELD_TYPE_GROUPS = [
  {
    id: "basic",
    label: "Basic",
    types: [
      { value: "text", label: "Text", icon: "fas fa-font" },
      { value: "textarea", label: "Text area", icon: "fas fa-align-left" },
      { value: "integer", label: "Integer", icon: "fas fa-hashtag" },
      { value: "number", label: "Number", icon: "fas fa-calculator" },
      { value: "calculated", label: "Calculated", icon: "fas fa-square-root-alt" },
    ],
  },
  {
    id: "choice",
    label: "Choice",
    types: [
      { value: "dropdown", label: "Dropdown", icon: "fas fa-caret-square-down" },
      { value: "radio", label: "Radio", icon: "fas fa-dot-circle" },
      { value: "multi_select", label: "Multi-select", icon: "fas fa-list-ul" },
      { value: "checkbox", label: "Checkbox", icon: "fas fa-check-square" },
      { value: "toggle", label: "Toggle", icon: "fas fa-toggle-on" },
      { value: "button_group", label: "Button group", icon: "fas fa-th-large" },
      { value: "yesno", label: "Yes / No", icon: "fas fa-thumbs-up" },
    ],
  },
  {
    id: "datetime",
    label: "Date & time",
    types: [
      { value: "date", label: "Date", icon: "fas fa-calendar-day" },
      { value: "datetime", label: "Date & time", icon: "fas fa-clock" },
      { value: "time", label: "Time", icon: "fas fa-hourglass-half" },
    ],
  },
  {
    id: "structure",
    label: "Structure",
    types: [
      {
        value: "repeating_table",
        label: "Repeating table",
        icon: "fas fa-table",
      },
    ],
  },
  {
    id: "layout",
    label: "Layout",
    types: [
      { value: "section", label: "Section", icon: "fas fa-heading" },
      { value: "heading", label: "Heading", icon: "fas fa-heading" },
      { value: "instruction", label: "Instruction", icon: "fas fa-info-circle" },
      { value: "divider", label: "Divider", icon: "fas fa-minus" },
      { value: "spacer", label: "Spacer", icon: "fas fa-arrows-alt-v" },
      { value: "pagebreak", label: "Page break", icon: "fas fa-file" },
    ],
  },
];

export const FIELD_TYPES = FIELD_TYPE_GROUPS.flatMap((g) => g.types);
export const ALL_FIELD_TYPES = FIELD_TYPES;

const CHOICE_TYPES = new Set([
  "dropdown",
  "radio",
  "multi_select",
  "button_group",
  "yesno",
  "toggle",
]);

const LAYOUT_TYPES = new Set([
  "section",
  "heading",
  "instruction",
  "divider",
  "spacer",
  "pagebreak",
]);

const NUMERIC_TYPES = new Set(["integer", "number"]);

const REPEATING_COLUMN_TYPES = [
  { value: "text", label: "Text" },
  { value: "integer", label: "Integer" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
];

export function getRepeatingColumnTypes() {
  return REPEATING_COLUMN_TYPES;
}

export function typeMeta(type) {
  return FIELD_TYPES.find((t) => t.value === type) || FIELD_TYPES[0];
}

export function getFieldTypeMeta(type) {
  return FIELD_TYPES.find((t) => t.value === type) || null;
}

export function isChoiceField(type) {
  return CHOICE_TYPES.has(type);
}

export function isChoiceWithOptions(type) {
  return CHOICE_TYPES.has(type);
}

export function isLayoutField(type) {
  return LAYOUT_TYPES.has(type);
}

export function isNumericField(type) {
  return NUMERIC_TYPES.has(type);
}

export function fieldSupportsValue(type) {
  return (
    !isLayoutField(type) && type !== "repeating_table" && type !== "calculated"
  );
}

function newId(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function createFieldId() {
  return newId("fld");
}

export function createLogicId() {
  return newId("log");
}

export function createColumnId() {
  return newId("col");
}

export function slugifyFieldName(label) {
  const raw = String(label || "").trim();
  if (!raw) return "field";
  const parts = raw
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "field";
  const [first, ...rest] = parts;
  let name =
    first.charAt(0).toLowerCase() +
    first.slice(1) +
    rest.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");
  name = name.replace(/[^a-zA-Z0-9_]/g, "");
  if (!name) return "field";
  if (/^[0-9]/.test(name)) name = `f_${name}`;
  return name.slice(0, 64);
}

export function ensureUniqueFieldName(name, fields, exceptId = null) {
  const base = slugifyFieldName(name) || "field";
  const used = new Set(
    (fields || [])
      .filter((f) => f && f.id !== exceptId)
      .map((f) => String(f.fieldName || "").trim().toLowerCase())
      .filter(Boolean),
  );
  if (!used.has(base.toLowerCase())) return base;
  let i = 2;
  while (used.has(`${base}${i}`.toLowerCase())) i += 1;
  return `${base}${i}`;
}

export function defaultOptionsForType(type) {
  if (type === "yesno") return ["Yes", "No"];
  if (type === "toggle") return ["Yes", "No"];
  if (
    type === "dropdown" ||
    type === "radio" ||
    type === "multi_select" ||
    type === "button_group"
  ) {
    return ["Option 1", "Option 2", "Option 3"];
  }
  return undefined;
}

export function defaultLabelForType(type) {
  if (type === "section") return "Section";
  if (type === "heading") return "Heading";
  if (type === "instruction") return "Instruction text";
  if (type === "divider") return "Divider";
  if (type === "spacer") return "Spacer";
  if (type === "pagebreak") return "Page break";
  if (type === "repeating_table") return "Repeating table";
  if (type === "calculated") return "Calculated value";
  if (type === "button_group") return "New field";
  if (type === "yesno") return "Yes / No";
  return "New field";
}

function normalizeOptionsLayout(raw) {
  const v = String(raw || "").toLowerCase();
  if (v === "horizontal" || v === "inline") return "inline";
  return "stacked";
}

function normalizeFormula(raw) {
  if (!raw || typeof raw !== "object") return null;
  const op = String(raw.operator || "").toLowerCase();
  if (["sum", "min", "max", "average"].includes(op)) {
    const fieldIds = Array.isArray(raw.fieldIds)
      ? raw.fieldIds.map(String).filter(Boolean)
      : [];
    return { operator: op, fieldIds };
  }
  if (["add", "subtract", "multiply", "divide"].includes(op)) {
    const leftId = raw.left?.fieldId || raw.leftFieldId || "";
    const rightId = raw.right?.fieldId || raw.rightFieldId || "";
    return {
      operator: op,
      left: { fieldId: String(leftId || "") },
      right: { fieldId: String(rightId || "") },
    };
  }
  return null;
}

export function createEmptyColumn(partial = {}, existingColumns = []) {
  const type = partial.type || "text";
  const label = partial.label || "Column";
  const id = partial.id || createColumnId();
  const fieldName =
    partial.fieldName ||
    ensureUniqueFieldName(
      label,
      (existingColumns || []).map((c) => ({
        id: c.id,
        fieldName: c.fieldName,
      })),
      id,
    );
  return {
    id,
    fieldName,
    label,
    type,
    required: Boolean(partial.required),
    width: partial.width === "full" ? "full" : "half",
    options: Array.isArray(partial.options)
      ? partial.options.map(String)
      : type === "dropdown"
        ? ["Option 1", "Option 2"]
        : [],
  };
}

export function createEmptyField(type = "text", overrides = {}) {
  const safeType = getFieldTypeMeta(type) ? type : "text";
  const id = overrides.id || createFieldId();
  const label =
    overrides.label != null ? overrides.label : defaultLabelForType(safeType);
  let fieldName = String(overrides.fieldName || "").trim();
  if (!fieldName) fieldName = slugifyFieldName(label);

  const field = {
    id,
    type: safeType,
    label,
    fieldName,
    required: Boolean(overrides.required),
    width:
      overrides.width === "half" ||
      overrides.width === "full" ||
      overrides.width === "third"
        ? overrides.width
        : isLayoutField(safeType) || safeType === "repeating_table"
          ? "full"
          : "half",
    placeholder: overrides.placeholder ?? "",
    helpText: overrides.helpText ?? "",
    defaultValue:
      overrides.defaultValue !== undefined
        ? overrides.defaultValue
        : safeType === "checkbox"
          ? false
          : "",
    maxLength: overrides.maxLength ?? "",
    minLength: overrides.minLength ?? "",
    pattern: overrides.pattern ?? "",
    minValue: overrides.minValue ?? "",
    maxValue: overrides.maxValue ?? "",
    step: overrides.step ?? "",
    decimalPlaces: overrides.decimalPlaces ?? "",
    unit: overrides.unit ?? "",
    minDate: overrides.minDate ?? "",
    maxDate: overrides.maxDate ?? "",
    dontAllowFuture: Boolean(
      overrides.dontAllowFuture ?? overrides.dontAllowFutureDate,
    ),
    dontAllowPast: Boolean(
      overrides.dontAllowPast ?? overrides.dontAllowPastDate,
    ),
    defaultCurrentDate: Boolean(overrides.defaultCurrentDate),
    optionsLayout: normalizeOptionsLayout(overrides.optionsLayout),
    formula: normalizeFormula(overrides.formula),
    columns: Array.isArray(overrides.columns)
      ? overrides.columns.map((c, i, arr) =>
          createEmptyColumn(c, arr.slice(0, i)),
        )
      : safeType === "repeating_table"
        ? [
            createEmptyColumn({ label: "Column 1", type: "text" }),
            createEmptyColumn({ label: "Column 2", type: "text" }),
          ]
        : [],
    minRows:
      overrides.minRows !== undefined && overrides.minRows !== ""
        ? overrides.minRows
        : safeType === "repeating_table"
          ? 1
          : "",
    maxRows:
      overrides.maxRows !== undefined && overrides.maxRows !== ""
        ? overrides.maxRows
        : safeType === "repeating_table"
          ? 20
          : "",
    addRowLabel: overrides.addRowLabel ?? "Add row",
  };

  if (isChoiceWithOptions(safeType)) {
    field.options = Array.isArray(overrides.options)
      ? overrides.options.map(String)
      : defaultOptionsForType(safeType) || [];
  }

  return field;
}

export function normalizeField(raw) {
  if (!raw || typeof raw !== "object") return null;
  let type = String(raw.type || "text").toLowerCase();
  if (type === "yes_no") type = "yesno";
  const known = getFieldTypeMeta(type);
  const safeType = known ? type : "text";
  const id = raw.id ? String(raw.id) : createFieldId();
  const label =
    raw.label != null && String(raw.label).trim()
      ? String(raw.label)
      : defaultLabelForType(safeType);

  let fieldName = String(raw.fieldName || raw.name || "").trim();
  if (!fieldName) fieldName = slugifyFieldName(label);

  const field = createEmptyField(safeType, {
    id,
    label,
    fieldName,
    required: Boolean(raw.required),
    width: raw.width,
    placeholder: raw.placeholder != null ? String(raw.placeholder) : "",
    helpText: raw.helpText != null ? String(raw.helpText) : "",
    defaultValue:
      raw.defaultValue !== undefined
        ? raw.defaultValue
        : safeType === "checkbox"
          ? false
          : "",
    maxLength: raw.maxLength ?? "",
    minLength: raw.minLength ?? "",
    pattern: raw.pattern != null ? String(raw.pattern) : "",
    minValue: raw.minValue ?? "",
    maxValue: raw.maxValue ?? "",
    step: raw.step ?? "",
    decimalPlaces: raw.decimalPlaces ?? "",
    unit: raw.unit != null ? String(raw.unit) : "",
    minDate: raw.minDate != null ? String(raw.minDate) : "",
    maxDate: raw.maxDate != null ? String(raw.maxDate) : "",
    dontAllowFuture: Boolean(raw.dontAllowFuture ?? raw.dontAllowFutureDate),
    dontAllowPast: Boolean(raw.dontAllowPast ?? raw.dontAllowPastDate),
    defaultCurrentDate: Boolean(raw.defaultCurrentDate),
    optionsLayout: raw.optionsLayout,
    options: Array.isArray(raw.options) ? raw.options : undefined,
    formula: raw.formula,
    columns: Array.isArray(raw.columns) ? raw.columns : undefined,
    minRows: raw.minRows,
    maxRows: raw.maxRows,
    addRowLabel: raw.addRowLabel != null ? String(raw.addRowLabel) : undefined,
  });

  if (safeType === "yesno") {
    field.options = ["Yes", "No"];
  } else if (safeType === "toggle") {
    const opts = Array.isArray(raw.options)
      ? raw.options.map(String).filter((o) => o.trim())
      : [];
    field.options = [opts[0] || "Yes", opts[1] || "No"];
  }

  return field;
}

export function normalizeLogicRule(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    fieldId: String(raw.fieldId || ""),
    operator: String(raw.operator || "equals"),
    value: raw.value != null ? String(raw.value) : "",
  };
}

export function normalizeLogicAction(raw) {
  if (!raw || typeof raw !== "object") return null;
  const action = String(raw.action || "show").toLowerCase();
  const allowed = new Set(["show", "hide", "enable", "disable"]);
  return {
    action: allowed.has(action) ? action : "show",
    targetId: String(raw.targetId || ""),
  };
}

export function normalizeLogicItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const match =
    String(raw.match || "all").toLowerCase() === "any" ? "any" : "all";
  const rules = Array.isArray(raw.rules)
    ? raw.rules.map(normalizeLogicRule).filter(Boolean)
    : [];
  const actions = Array.isArray(raw.actions)
    ? raw.actions.map(normalizeLogicAction).filter(Boolean)
    : [];
  return {
    id: raw.id ? String(raw.id) : createLogicId(),
    match,
    rules,
    actions,
  };
}

export function createEmptyLogicItem() {
  return {
    id: createLogicId(),
    match: "all",
    rules: [{ fieldId: "", operator: "equals", value: "" }],
    actions: [{ action: "show", targetId: "" }],
  };
}

export function normalizeFormSettings(raw) {
  const layout =
    String(raw?.layout || "two-column").toLowerCase() === "one-column"
      ? "one-column"
      : "two-column";
  return { layout };
}

export function parseSchemaJson(schemaJson) {
  if (!schemaJson) {
    return {
      fields: [],
      fillAccess: FILL_ACCESS_INTERNAL,
      formHeader: "",
      formDescription: "",
      settings: normalizeFormSettings(null),
      logic: [],
    };
  }
  try {
    const parsed =
      typeof schemaJson === "string" ? JSON.parse(schemaJson) : schemaJson;
    const fields = Array.isArray(parsed?.fields)
      ? parsed.fields.map(normalizeField).filter(Boolean)
      : [];
    const seen = new Map();
    fields.forEach((f) => {
      const key = String(f.fieldName || "").toLowerCase();
      if (!key || seen.has(key)) {
        f.fieldName = ensureUniqueFieldName(
          f.fieldName || f.label,
          fields,
          f.id,
        );
      }
      seen.set(String(f.fieldName).toLowerCase(), f.id);
    });
    return {
      fields,
      fillAccess: normalizeFillAccess(parsed?.fillAccess),
      formHeader:
        parsed?.formHeader != null
          ? normalizeFormHeader(parsed.formHeader)
          : "",
      formDescription:
        parsed?.formDescription != null
          ? normalizeFormDescription(parsed.formDescription)
          : "",
      settings: normalizeFormSettings(parsed?.settings),
      logic: Array.isArray(parsed?.logic)
        ? parsed.logic.map(normalizeLogicItem).filter(Boolean)
        : [],
    };
  } catch {
    return {
      fields: [],
      fillAccess: FILL_ACCESS_INTERNAL,
      formHeader: "",
      formDescription: "",
      settings: normalizeFormSettings(null),
      logic: [],
    };
  }
}

function serializeField(f) {
  const out = {
    id: f.id,
    type: f.type,
    label: f.label,
    fieldName: f.fieldName,
    required: Boolean(f.required),
    width: f.width === "full" ? "full" : f.width === "third" ? "third" : "half",
  };
  if (f.placeholder) out.placeholder = f.placeholder;
  if (f.helpText) out.helpText = f.helpText;
  if (f.defaultValue !== undefined && f.defaultValue !== "" && f.defaultValue !== false) {
    out.defaultValue = f.defaultValue;
  }
  if (f.type === "checkbox" && f.defaultValue) out.defaultValue = true;
  if (f.maxLength !== "" && f.maxLength != null && !Number.isNaN(Number(f.maxLength))) {
    out.maxLength = Number(f.maxLength);
  }
  if (f.minLength !== "" && f.minLength != null && !Number.isNaN(Number(f.minLength))) {
    out.minLength = Number(f.minLength);
  }
  if (f.pattern) out.pattern = f.pattern;
  if (f.minValue !== "" && f.minValue != null && !Number.isNaN(Number(f.minValue))) {
    out.minValue = Number(f.minValue);
  }
  if (f.maxValue !== "" && f.maxValue != null && !Number.isNaN(Number(f.maxValue))) {
    out.maxValue = Number(f.maxValue);
  }
  if (f.step !== "" && f.step != null && !Number.isNaN(Number(f.step))) {
    out.step = Number(f.step);
  }
  if (
    f.decimalPlaces !== "" &&
    f.decimalPlaces != null &&
    !Number.isNaN(Number(f.decimalPlaces))
  ) {
    out.decimalPlaces = Number(f.decimalPlaces);
  }
  if (f.unit) out.unit = f.unit;
  if (f.minDate) out.minDate = f.minDate;
  if (f.maxDate) out.maxDate = f.maxDate;
  if (f.dontAllowFuture) out.dontAllowFuture = true;
  if (f.dontAllowPast) out.dontAllowPast = true;
  if (f.defaultCurrentDate) out.defaultCurrentDate = true;
  if (isChoiceWithOptions(f.type)) {
    out.optionsLayout = normalizeOptionsLayout(f.optionsLayout);
    out.options = Array.isArray(f.options) ? f.options.map(String) : [];
  }
  if (f.type === "calculated" && f.formula) {
    out.formula = f.formula;
  }
  if (f.type === "repeating_table") {
    out.addRowLabel = f.addRowLabel || "Add row";
    if (f.minRows !== "" && f.minRows != null) out.minRows = Number(f.minRows);
    if (f.maxRows !== "" && f.maxRows != null) out.maxRows = Number(f.maxRows);
    out.columns = Array.isArray(f.columns)
      ? f.columns.map((c) => ({
          id: c.id,
          fieldName: c.fieldName,
          label: c.label,
          type: c.type,
          required: Boolean(c.required),
          width: c.width === "full" ? "full" : "half",
          ...(c.type === "dropdown"
            ? {
                options: Array.isArray(c.options)
                  ? c.options.map(String)
                  : [],
              }
            : {}),
        }))
      : [];
  }
  return out;
}

export function buildSchemaJson({
  fields,
  fillAccess,
  formHeader,
  formDescription,
  settings,
  logic,
}) {
  const payload = {
    fields: (fields || []).map(serializeField),
    fillAccess: normalizeFillAccess(fillAccess),
    settings: normalizeFormSettings(settings),
    logic: Array.isArray(logic)
      ? logic.map(normalizeLogicItem).filter(Boolean)
      : [],
  };
  const header = normalizeFormHeader(formHeader || "").trim();
  const description = normalizeFormDescription(formDescription || "").trim();
  if (header) payload.formHeader = header;
  if (description) payload.formDescription = description;
  return JSON.stringify(payload);
}

export function fieldWidthClass(width) {
  if (width === "half") return "col-md-6";
  if (width === "third") return "col-md-4";
  return "col-12";
}

export function isFieldValueEmpty(field, value) {
  if (field.type === "checkbox") return !value;
  if (field.type === "repeating_table") {
    const rows = Array.isArray(value) ? value : [];
    const min = Number(field.minRows) || 0;
    return rows.length < Math.max(min, field.required ? 1 : 0);
  }
  if (Array.isArray(value)) return value.length === 0;
  return value == null || String(value).trim() === "";
}

export function collectRequiredMissing(fields, values, visibilityMap = null) {
  return (fields || []).filter((f) => {
    if (!f.required || isLayoutField(f.type) || f.type === "calculated") {
      return false;
    }
    if (visibilityMap && visibilityMap[f.id] === false) return false;
    return isFieldValueEmpty(f, values?.[f.id]);
  });
}

/** True when pattern means digits-only (optionally fixed/variable length). */
export function isDigitsOnlyPattern(pattern) {
  const p = String(pattern || "").trim();
  if (!p) return false;
  return (
    /^\^\\d\+\$/.test(p) ||
    /^\^\[0-9\]\+\$/.test(p) ||
    /^\^\\d\{\d+(?:,\d*)?\}\$/.test(p) ||
    /^\^\[0-9\]\{\d+(?:,\d*)?\}\$/.test(p)
  );
}

/**
 * Runtime value checks for max/min length, pattern, min/max numeric.
 * Returns { id, label, message }[] — empty when valid.
 */
export function collectFieldValidationIssues(
  fields,
  values,
  visibilityMap = null,
) {
  const issues = [];
  (fields || []).forEach((f) => {
    if (!f || isLayoutField(f.type) || f.type === "calculated") return;
    if (visibilityMap && visibilityMap[f.id] === false) return;
    const raw = values?.[f.id];
    if (isFieldValueEmpty(f, raw)) return;

    const label = String(f.label || f.fieldName || "Field").trim() || "Field";
    const text = Array.isArray(raw) ? raw.join(", ") : String(raw);

    if (f.type === "text" || f.type === "textarea") {
      const minLen =
        f.minLength !== "" && f.minLength != null ? Number(f.minLength) : null;
      const maxLen =
        f.maxLength !== "" && f.maxLength != null ? Number(f.maxLength) : null;
      if (Number.isFinite(minLen) && text.length < minLen) {
        issues.push({
          id: f.id,
          label,
          message: `${label} must be at least ${minLen} character(s).`,
        });
      }
      if (Number.isFinite(maxLen) && text.length > maxLen) {
        issues.push({
          id: f.id,
          label,
          message: `${label} must be at most ${maxLen} character(s).`,
        });
      }
      const pattern = String(f.pattern || "").trim();
      if (pattern) {
        try {
          const re = new RegExp(pattern);
          if (!re.test(text)) {
            issues.push({
              id: f.id,
              label,
              message: isDigitsOnlyPattern(pattern)
                ? `${label} must contain numbers only${
                    Number.isFinite(minLen) &&
                    Number.isFinite(maxLen) &&
                    minLen === maxLen
                      ? ` (${minLen} digits)`
                      : ""
                  }.`
                : `${label} does not match the required format.`,
            });
          }
        } catch {
          /* invalid pattern already blocked at design-time */
        }
      }
    }

    if (f.type === "integer" || f.type === "number") {
      const num = Number(text);
      if (Number.isNaN(num)) {
        issues.push({
          id: f.id,
          label,
          message: `${label} must be a valid number.`,
        });
        return;
      }
      if (f.type === "integer" && !Number.isInteger(num)) {
        issues.push({
          id: f.id,
          label,
          message: `${label} must be a whole number.`,
        });
      }
      const minVal =
        f.minValue !== "" && f.minValue != null ? Number(f.minValue) : null;
      const maxVal =
        f.maxValue !== "" && f.maxValue != null ? Number(f.maxValue) : null;
      if (Number.isFinite(minVal) && num < minVal) {
        issues.push({
          id: f.id,
          label,
          message: `${label} must be at least ${minVal}.`,
        });
      }
      if (Number.isFinite(maxVal) && num > maxVal) {
        issues.push({
          id: f.id,
          label,
          message: `${label} must be at most ${maxVal}.`,
        });
      }
    }
  });
  return issues;
}

export function applyFieldDefaults(fields, existingValues = {}) {
  const next = { ...existingValues };
  (fields || []).forEach((field) => {
    if (isLayoutField(field.type)) return;
    if (field.type === "calculated") {
      if (next[field.id] == null) next[field.id] = "";
      return;
    }
    if (field.type === "repeating_table") {
      if (Array.isArray(next[field.id])) return;
      const min = Math.max(0, Number(field.minRows) || 0);
      const cols = Array.isArray(field.columns) ? field.columns : [];
      const rowCount = Math.max(min, 1);
      next[field.id] = Array.from({ length: rowCount }, () => {
        const row = {};
        cols.forEach((c) => {
          row[c.fieldName] = "";
        });
        return row;
      });
      return;
    }
    if (next[field.id] != null && next[field.id] !== "") return;
    if (field.type === "checkbox") {
      next[field.id] = !!field.defaultValue;
      return;
    }
    if (field.type === "multi_select") {
      if (Array.isArray(field.defaultValue)) {
        next[field.id] = field.defaultValue;
      } else if (field.defaultValue) {
        next[field.id] = [String(field.defaultValue)];
      } else {
        next[field.id] = [];
      }
      return;
    }
    if (
      field.defaultCurrentDate &&
      (field.type === "date" || field.type === "datetime")
    ) {
      const now = new Date();
      if (field.type === "date") {
        next[field.id] = now.toISOString().slice(0, 10);
      } else {
        const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
        next[field.id] = local.toISOString().slice(0, 16);
      }
      return;
    }
    if (field.defaultValue != null && field.defaultValue !== "") {
      next[field.id] = field.defaultValue;
    }
  });
  return next;
}

export function dateInputBounds(field) {
  const today = new Date().toISOString().slice(0, 10);
  const attrs = {};
  if (field.minDate) attrs.min = field.minDate;
  if (field.maxDate) attrs.max = field.maxDate;
  if (field.dontAllowFuture) {
    attrs.max = attrs.max && attrs.max < today ? attrs.max : today;
  }
  if (field.dontAllowPast) {
    attrs.min = attrs.min && attrs.min > today ? attrs.min : today;
  }
  return attrs;
}

const PLACEHOLDER_LABELS = new Set([
  "new field",
  "untitled",
  "untitled field",
  "field",
  "label",
  "test",
  "asdf",
  "xxx",
]);

const PLACEHOLDER_OPTIONS = new Set([
  "option 1",
  "option 2",
  "option 3",
  "option1",
  "option2",
  "option3",
  "opt 1",
  "opt 2",
  "opt 3",
  "choose",
  "select",
  "test",
  "asdf",
  "xxx",
]);

function isUnrealisticLabel(label) {
  const t = String(label || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!t) return true;
  if (PLACEHOLDER_LABELS.has(t)) return true;
  if (/^new field(\s+\d+)?$/.test(t)) return true;
  if (/^field\s*\d*$/.test(t)) return true;
  if (/^test\s*\d*$/.test(t)) return true;
  if (/^(asdf|qwer|zxcv)+$/.test(t)) return true;
  if (/^x{2,}$/.test(t)) return true;
  return false;
}

function isUnrealisticOption(opt) {
  const t = String(opt || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!t) return true;
  if (PLACEHOLDER_OPTIONS.has(t)) return true;
  if (/^option\s*\d+$/.test(t)) return true;
  if (/^opt(ion)?\s*\d+$/.test(t)) return true;
  if (/^(asdf|qwer|zxcv)+$/.test(t)) return true;
  if (/^x{2,}$/.test(t)) return true;
  return false;
}

function isBlankish(value) {
  return value == null || String(value).trim() === "";
}

function pushIssue(issues, fieldId, message) {
  issues.push({ fieldId: fieldId || null, message });
}

/** Design-time validation — returns issue array (builder contract). */
export function validateTemplateDesign(fields, logic = []) {
  const list = Array.isArray(fields) ? fields : [];
  const issues = [];

  if (!list.length) {
    pushIssue(
      issues,
      null,
      "Add at least one field before saving this form draft.",
    );
    return issues;
  }

  const ids = new Set();
  const fieldNames = new Map();
  const labels = new Map();

  list.forEach((f, index) => {
    const pos = index + 1;
    const typeLabel = typeMeta(f.type)?.label || f.type || "Field";
    const where = `Field ${pos} (${typeLabel})`;

    if (!f.id || ids.has(f.id)) {
      pushIssue(issues, f.id, `${where}: duplicate or missing field id.`);
    } else {
      ids.add(f.id);
    }

    const fn = String(f.fieldName || "").trim();
    if (!fn) {
      pushIssue(issues, f.id, `${where}: field name is required.`);
    } else {
      const key = fn.toLowerCase();
      if (fieldNames.has(key)) {
        pushIssue(
          issues,
          f.id,
          `${where}: field name "${fn}" is already used by another field.`,
        );
      } else {
        fieldNames.set(key, f.id);
      }
    }

    const needsLabel = !["divider", "spacer", "pagebreak"].includes(f.type);
    if (needsLabel && isBlankish(f.label)) {
      pushIssue(issues, f.id, `${where}: label is required.`);
    } else if (
      needsLabel &&
      !isLayoutField(f.type) &&
      isUnrealisticLabel(f.label)
    ) {
      pushIssue(
        issues,
        f.id,
        `${where}: replace placeholder label "${String(f.label).trim()}" with a real question or field name.`,
      );
    } else if (needsLabel && !isBlankish(f.label) && !isLayoutField(f.type)) {
      const labelKey = String(f.label).trim().toLowerCase();
      if (labels.has(labelKey)) {
        pushIssue(
          issues,
          f.id,
          `${where}: label "${String(f.label).trim()}" is already used by another field.`,
        );
      } else {
        labels.set(labelKey, f.id);
      }
    }

    if (isChoiceWithOptions(f.type) && f.type !== "checkbox") {
      const opts = Array.isArray(f.options)
        ? f.options.map((o) => String(o).trim()).filter(Boolean)
        : [];
      if (f.type === "yesno") {
        if (opts.length !== 2) {
          pushIssue(
            issues,
            f.id,
            `${where}: Yes/No must have exactly two options.`,
          );
        }
      } else if (f.type === "toggle") {
        if (opts.length !== 2) {
          pushIssue(
            issues,
            f.id,
            `${where}: Toggle needs exactly two labels (on/off).`,
          );
        } else if (opts.some(isUnrealisticOption) || opts[0] === opts[1]) {
          pushIssue(
            issues,
            f.id,
            `${where}: set two distinct, realistic toggle labels.`,
          );
        }
      } else if (opts.length < 2) {
        pushIssue(issues, f.id, `${where}: add at least two options.`);
      } else {
        const lower = opts.map((o) => o.toLowerCase());
        if (new Set(lower).size !== lower.length) {
          pushIssue(issues, f.id, `${where}: option labels must be unique.`);
        }
        const bad = opts.filter(isUnrealisticOption);
        if (bad.length) {
          pushIssue(
            issues,
            f.id,
            `${where}: replace placeholder options (${bad
              .slice(0, 3)
              .map((o) => `"${o}"`)
              .join(", ")}) with real choices.`,
          );
        }
      }
    }

    if (f.type === "text" || f.type === "textarea") {
      const minL =
        f.minLength !== "" && f.minLength != null ? Number(f.minLength) : null;
      const maxL =
        f.maxLength !== "" && f.maxLength != null ? Number(f.maxLength) : null;
      if (minL != null && Number.isNaN(minL)) {
        pushIssue(issues, f.id, `${where}: min length must be a number.`);
      }
      if (maxL != null && Number.isNaN(maxL)) {
        pushIssue(issues, f.id, `${where}: max length must be a number.`);
      }
      if (minL != null && maxL != null && minL > maxL) {
        pushIssue(
          issues,
          f.id,
          `${where}: min length cannot be greater than max length.`,
        );
      }
      if (f.pattern && String(f.pattern).trim()) {
        try {
          // eslint-disable-next-line no-new
          new RegExp(String(f.pattern));
        } catch {
          pushIssue(
            issues,
            f.id,
            `${where}: pattern is not a valid regular expression.`,
          );
        }
      }
    }

    if (f.type === "integer" || f.type === "number") {
      const min =
        f.minValue !== "" && f.minValue != null ? Number(f.minValue) : null;
      const max =
        f.maxValue !== "" && f.maxValue != null ? Number(f.maxValue) : null;
      if (min != null && Number.isNaN(min)) {
        pushIssue(issues, f.id, `${where}: minimum must be a number.`);
      }
      if (max != null && Number.isNaN(max)) {
        pushIssue(issues, f.id, `${where}: maximum must be a number.`);
      }
      if (min != null && max != null && min > max) {
        pushIssue(
          issues,
          f.id,
          `${where}: minimum cannot be greater than maximum.`,
        );
      }
    }

    if (f.type === "date" || f.type === "datetime") {
      if (f.dontAllowFuture && f.dontAllowPast) {
        pushIssue(
          issues,
          f.id,
          `${where}: cannot disallow both future and past dates.`,
        );
      }
      if (f.minDate && f.maxDate && f.minDate > f.maxDate) {
        pushIssue(issues, f.id, `${where}: min date cannot be after max date.`);
      }
    }

    if (f.type === "repeating_table") {
      const cols = Array.isArray(f.columns) ? f.columns : [];
      if (!cols.length) {
        pushIssue(issues, f.id, `${where}: add at least one column.`);
      }
      const colNames = new Map();
      cols.forEach((c, ci) => {
        const cWhere = `${where} column ${ci + 1}`;
        if (isBlankish(c.label) || isUnrealisticLabel(c.label)) {
          pushIssue(issues, f.id, `${cWhere}: set a realistic column label.`);
        }
        const cfn = String(c.fieldName || "").trim();
        if (!cfn) {
          pushIssue(issues, f.id, `${cWhere}: column field name is required.`);
        } else if (colNames.has(cfn.toLowerCase())) {
          pushIssue(
            issues,
            f.id,
            `${cWhere}: column field name "${cfn}" is duplicated.`,
          );
        } else {
          colNames.set(cfn.toLowerCase(), c.id);
        }
        if (c.type === "dropdown") {
          const opts = Array.isArray(c.options)
            ? c.options.map((o) => String(o).trim()).filter(Boolean)
            : [];
          if (opts.length < 2) {
            pushIssue(
              issues,
              f.id,
              `${cWhere}: dropdown needs at least two options.`,
            );
          }
        }
      });
      const minR =
        f.minRows !== "" && f.minRows != null ? Number(f.minRows) : null;
      const maxR =
        f.maxRows !== "" && f.maxRows != null ? Number(f.maxRows) : null;
      if (minR != null && maxR != null && minR > maxR) {
        pushIssue(
          issues,
          f.id,
          `${where}: min rows cannot be greater than max rows.`,
        );
      }
    }

    if (f.type === "calculated") {
      const formula = f.formula;
      if (!formula || !formula.operator) {
        pushIssue(issues, f.id, `${where}: configure a formula.`);
      } else if (["sum", "min", "max", "average"].includes(formula.operator)) {
        const idsRef = Array.isArray(formula.fieldIds) ? formula.fieldIds : [];
        if (idsRef.length < 1) {
          pushIssue(
            issues,
            f.id,
            `${where}: select at least one numeric field for the formula.`,
          );
        }
        idsRef.forEach((rid) => {
          const src = list.find((x) => x.id === rid);
          if (!src) {
            pushIssue(
              issues,
              f.id,
              `${where}: formula references a missing field.`,
            );
          } else if (!isNumericField(src.type)) {
            pushIssue(
              issues,
              f.id,
              `${where}: formula field "${src.label}" must be integer or number.`,
            );
          }
        });
      } else {
        const left = list.find((x) => x.id === formula.left?.fieldId);
        const right = list.find((x) => x.id === formula.right?.fieldId);
        if (!left || !right) {
          pushIssue(
            issues,
            f.id,
            `${where}: formula left/right fields are missing.`,
          );
        } else {
          [left, right].forEach((src) => {
            if (!isNumericField(src.type)) {
              pushIssue(
                issues,
                f.id,
                `${where}: "${src.label}" must be integer or number.`,
              );
            }
          });
        }
      }
    }
  });

  const logicList = Array.isArray(logic) ? logic : [];
  logicList.forEach((item, li) => {
    const where = `Logic rule ${li + 1}`;
    (item.rules || []).forEach((r) => {
      if (!r.fieldId || !ids.has(r.fieldId)) {
        pushIssue(
          issues,
          null,
          `${where}: condition references a missing field.`,
        );
      }
    });
    (item.actions || []).forEach((a) => {
      if (!a.targetId || !ids.has(a.targetId)) {
        pushIssue(issues, null, `${where}: action targets a missing field.`);
      }
    });
  });

  return issues;
}

export function findFieldReferences(fieldId, fields, logic = []) {
  const refs = [];
  (fields || []).forEach((f) => {
    if (f.type !== "calculated" || !f.formula) return;
    const formula = f.formula;
    if (Array.isArray(formula.fieldIds) && formula.fieldIds.includes(fieldId)) {
      refs.push({ kind: "formula", field: f });
    }
    if (
      formula.left?.fieldId === fieldId ||
      formula.right?.fieldId === fieldId
    ) {
      refs.push({ kind: "formula", field: f });
    }
  });
  (logic || []).forEach((item) => {
    const inRules = (item.rules || []).some((r) => r.fieldId === fieldId);
    const inActions = (item.actions || []).some((a) => a.targetId === fieldId);
    if (inRules || inActions) {
      refs.push({ kind: "logic", logicId: item.id });
    }
  });
  return refs;
}

export function evaluateLogicVisibility(fields, values, logic = []) {
  const visible = {};
  const enabled = {};
  (fields || []).forEach((f) => {
    visible[f.id] = true;
    enabled[f.id] = true;
  });

  const getVal = (fieldId) => values?.[fieldId];

  const matchRule = (rule) => {
    const v = getVal(rule.fieldId);
    const op = rule.operator;
    const cmp = rule.value;
    const str = v == null ? "" : Array.isArray(v) ? v.join(",") : String(v);
    switch (op) {
      case "equals":
        return str === String(cmp);
      case "notEquals":
        return str !== String(cmp);
      case "contains":
        return str.toLowerCase().includes(String(cmp).toLowerCase());
      case "notContains":
        return !str.toLowerCase().includes(String(cmp).toLowerCase());
      case "gt":
        return Number(str) > Number(cmp);
      case "lt":
        return Number(str) < Number(cmp);
      case "gte":
        return Number(str) >= Number(cmp);
      case "lte":
        return Number(str) <= Number(cmp);
      case "isEmpty":
        return str.trim() === "" || (Array.isArray(v) && v.length === 0);
      case "isNotEmpty":
        return !(str.trim() === "" || (Array.isArray(v) && v.length === 0));
      default:
        return false;
    }
  };

  (logic || []).forEach((item) => {
    const rules = item.rules || [];
    if (!rules.length) return;
    const ok =
      item.match === "any" ? rules.some(matchRule) : rules.every(matchRule);
    if (!ok) return;
    (item.actions || []).forEach((a) => {
      if (!a.targetId) return;
      if (a.action === "show") visible[a.targetId] = true;
      if (a.action === "hide") visible[a.targetId] = false;
      if (a.action === "enable") enabled[a.targetId] = true;
      if (a.action === "disable") enabled[a.targetId] = false;
    });
  });

  return { visible, enabled };
}

export function evaluateCalculatedValue(field, fields, values) {
  const formula = field?.formula;
  if (!formula) return "";
  const num = (id) => {
    const n = Number(values?.[id]);
    return Number.isFinite(n) ? n : NaN;
  };
  if (["sum", "min", "max", "average"].includes(formula.operator)) {
    const nums = (formula.fieldIds || [])
      .map(num)
      .filter((n) => Number.isFinite(n));
    if (!nums.length) return "";
    if (formula.operator === "sum") return nums.reduce((a, b) => a + b, 0);
    if (formula.operator === "min") return Math.min(...nums);
    if (formula.operator === "max") return Math.max(...nums);
    if (formula.operator === "average") {
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    }
  }
  const left = num(formula.left?.fieldId);
  const right = num(formula.right?.fieldId);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return "";
  switch (formula.operator) {
    case "add":
      return left + right;
    case "subtract":
      return left - right;
    case "multiply":
      return left * right;
    case "divide":
      return right === 0 ? "" : left / right;
    default:
      return "";
  }
}

export function duplicateFieldInList(fields, sourceId) {
  const list = Array.isArray(fields) ? [...fields] : [];
  const idx = list.findIndex((f) => f.id === sourceId);
  if (idx < 0) return list;
  const src = list[idx];
  const newId = createFieldId();
  const label = `${src.label || "Field"} Copy`;
  const clone = normalizeField({
    ...JSON.parse(JSON.stringify(src)),
    id: newId,
    label,
    fieldName: "",
  });
  clone.fieldName = ensureUniqueFieldName(label, list, newId);
  if (clone.type === "repeating_table" && Array.isArray(clone.columns)) {
    clone.columns = clone.columns.map((c) =>
      createEmptyColumn({
        ...c,
        id: createColumnId(),
      }),
    );
  }
  list.splice(idx + 1, 0, clone);
  return list;
}

export const LOGIC_OPERATORS = [
  { value: "equals", label: "equals" },
  { value: "notEquals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "notContains", label: "does not contain" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
  { value: "gte", label: "greater or equal" },
  { value: "lte", label: "less or equal" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];

export const LOGIC_ACTIONS = [
  { value: "show", label: "Show" },
  { value: "hide", label: "Hide" },
  { value: "enable", label: "Enable" },
  { value: "disable", label: "Disable" },
];

export const FORMULA_OPERATORS = [
  { value: "add", label: "Add (+)" },
  { value: "subtract", label: "Subtract (−)" },
  { value: "multiply", label: "Multiply (×)" },
  { value: "divide", label: "Divide (÷)" },
  { value: "sum", label: "Sum" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
  { value: "average", label: "Average" },
];
